import { MutationFn, UseQueryFn } from 'shared-ui';

import {
  DodCapabilities,
  DodDlqArgs,
  DodDlqBulkRequest,
  DodDlqPurgeResponse,
  DodDlqResponse,
  DodDlqRetryResponse,
  DodDlqSummaryArgs,
  DodDlqSummaryResponse,
  DodHistoryArgs,
  DodHistoryResponse,
  DodOverviewArgs,
  DodOverviewResponse,
  DodPromqlArgs,
  DodPromqlResponse,
  DodQueueActionArgs,
  DodQueueActionResponse,
  DodQueueDetailArgs,
  DodQueuesArgs,
  DodQueuesResponse,
  DodTimeseriesArgs,
  DodTimeseriesResponse,
  DodWorkerArgs,
  DodWorkerResponse,
  QueueDetailResponseV2,
} from './dod.v2.types';

/**
 * Fetchers for every route in `DOD_API_ROUTES` (the v2 half).
 *
 * Two shared-ui mechanics shape every signature here:
 *  - `useQuery` serialises the WHOLE args object onto the query string
 *    (`queryString.stringify(args, { arrayFormat: 'comma' })`), so args must be flat —
 *    no nested objects, no arrays of objects. Path parameters therefore appear twice
 *    (in the path and as a harmless echo in the query string); the backend ignores the echo.
 *  - `useMutation` takes its params from the mutate() call, so path parameters and the body
 *    travel together in one flat argument object.
 */

// ── Capabilities ─────────────────────────────────────────────────────

/**
 * Capability probe. The shell calls this before any tab mounts, to hide Prometheus-only controls
 * and to tell "no data" apart from "no rollup provider".
 */
export const fetchDodCapabilities: UseQueryFn<void, DodCapabilities> = () => {
  return {
    endpoint: '/dod/capabilities',
  };
};

// ── Overview ─────────────────────────────────────────────────────────

export const fetchDodOverview: UseQueryFn<DodOverviewArgs, DodOverviewResponse> = () => {
  return {
    endpoint: '/dod/overview',
  };
};

// ── Time series ──────────────────────────────────────────────────────

/**
 * Every arg reaches the route through the shared serialiser, including `dim` and the `groupBy`
 * array, which goes out comma-separated. Nothing here needs to build the query string by hand.
 */
export const fetchDodTimeseries: UseQueryFn<DodTimeseriesArgs, DodTimeseriesResponse> = () => {
  return {
    endpoint: '/dod/timeseries',
  };
};

// ── Queues ───────────────────────────────────────────────────────────

export const fetchDodQueues: UseQueryFn<DodQueuesArgs, DodQueuesResponse> = () => {
  return {
    endpoint: '/dod/queues',
  };
};

export const fetchDodQueueDetail: UseQueryFn<DodQueueDetailArgs, QueueDetailResponseV2> = (args) => {
  return {
    endpoint: `/dod/queue/${args?.rcpId ?? ''}/${args?.nlk ?? 0}`,
  };
};

export const drainDodQueue: MutationFn<DodQueueActionArgs, DodQueueActionResponse> = ({ rcpId, nlk }) => {
  return {
    endpoint: `/dod/queue/${rcpId}/${nlk}/drain`,
    method: 'POST',
  };
};

export const pauseDodQueue: MutationFn<DodQueueActionArgs, DodQueueActionResponse> = ({ rcpId, nlk }) => {
  return {
    endpoint: `/dod/queue/${rcpId}/${nlk}/pause`,
    method: 'POST',
  };
};

export const resumeDodQueue: MutationFn<DodQueueActionArgs, DodQueueActionResponse> = ({ rcpId, nlk }) => {
  return {
    endpoint: `/dod/queue/${rcpId}/${nlk}/resume`,
    method: 'POST',
  };
};

// ── Dead letter ──────────────────────────────────────────────────────

export const fetchDodDlq: UseQueryFn<DodDlqArgs, DodDlqResponse> = () => {
  return {
    endpoint: '/dod/dlq',
  };
};

export const fetchDodDlqSummary: UseQueryFn<DodDlqSummaryArgs, DodDlqSummaryResponse> = () => {
  return {
    endpoint: '/dod/dlq/summary',
  };
};

export const retryDodDlq: MutationFn<DodDlqBulkRequest, DodDlqRetryResponse> = (body) => {
  return {
    endpoint: '/dod/dlq/retry',
    method: 'POST',
    body,
  };
};

export const purgeDodDlq: MutationFn<DodDlqBulkRequest, DodDlqPurgeResponse> = (body) => {
  return {
    endpoint: '/dod/dlq/purge',
    method: 'DELETE',
    body,
  };
};

// ── History ──────────────────────────────────────────────────────────

export const fetchDodHistory: UseQueryFn<DodHistoryArgs, DodHistoryResponse> = () => {
  return {
    endpoint: '/dod/history',
  };
};

/**
 * CSV export of the current history filter. Returned as a blob so the caller can save it;
 * pass the same args as `fetchDodHistory` plus `format: 'csv'`.
 */
export const fetchDodHistoryCsv: UseQueryFn<DodHistoryArgs, Blob> = () => {
  return {
    endpoint: '/dod/history',
    responseType: 'blob',
  };
};

// ── Worker ───────────────────────────────────────────────────────────

export const fetchDodWorker: UseQueryFn<DodWorkerArgs, DodWorkerResponse> = () => {
  return {
    endpoint: '/dod/worker',
  };
};

// ── PromQL proxy ─────────────────────────────────────────────────────

/** Optional. 404s when PROMETHEUS_URL is unset — gate on `capabilities.prometheus`. */
export const fetchDodPromql: UseQueryFn<DodPromqlArgs, DodPromqlResponse> = () => {
  return {
    endpoint: '/dod/promql',
  };
};
